import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  // FolderSpecial as ProjectIcon,
  // Terrain as SiteIcon,
  // Sensors as DeploymentIcon,
  // CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { useLocation, useNavigate } from 'react-router-dom';
import { Project } from 'src/api/Api';
import Header from 'src/view/components/navigation/Header';
import UploadAudioModal from './components/UploadAudioModal';
import AudioAnalyzerPopup from './components/AudioAnalyzerPopup';
import SpeciesAvgActivityModal from './components/SpeciesAvgActivityModal';
// import ProjectLevelSection from './components/ProjectLevelSection';
// import SiteLevelSection from './components/SiteLevelSection';
// import DeploymentLevelSection from './components/DeploymentLevelSection';
// import ProjectDeploymentComparisonSection from './components/ProjectDeploymentComparisonSection';
import BioacousticAnalysis from './components/BioacousticAnalysis';
import SpeciesCatalog from './components/SpeciesCatalog';
import { isTataProject } from './components/speciesCatalog/projectAccess';
import AcousticDataView from './components/AcousticDataView';
import { ProjectWithSites } from 'src/state-management/state';
import { Site } from 'src/api/Api';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { addSiteBoundaryWithColor } from 'src/view/InteractiveMap/map-helper';
import { getColorByIndex } from 'src/mapbox-helpers/color-utils';
import {
  MOCK_PROJECT_HIERARCHY,
  BioSite,
  BioDeployment,
  // MOCK_SPECIES_CATALOG_EXTENDED,
} from './bioacousticMockData';

const buildMarkerSVG = (ringColor: string, iconPath: string) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
    <defs>
      <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    <!-- Outer glow ring -->
    <circle cx="26" cy="26" r="24" fill="none" stroke="${ringColor}" stroke-width="3" filter="url(#glow)" opacity="0.85"/>
    <!-- Dark disc background -->
    <circle cx="26" cy="26" r="21" fill="#0f0f1a" opacity="0.92"/>
    <!-- Inner ring -->
    <circle cx="26" cy="26" r="21" fill="none" stroke="${ringColor}" stroke-width="1.5" opacity="0.6"/>
    <!-- White icon centred -->
    <g transform="translate(12, 12) scale(1.16)" fill="white">
      ${iconPath}
    </g>
  </svg>
`;
const AUDIO_ICON_PATH = `
  <rect x="1"  y="7"  width="2.5" height="10" rx="1.2"/>
  <rect x="5"  y="3"  width="2.5" height="18" rx="1.2"/>
  <rect x="9"  y="5"  width="2.5" height="14" rx="1.2"/>
  <rect x="13" y="1"  width="2.5" height="22" rx="1.2"/>
  <rect x="17" y="6"  width="2.5" height="12" rx="1.2"/>
  <rect x="21" y="9"  width="2.5" height="6"  rx="1.2"/>
`;

const MULTIPLE_MARKER_PATH = `
  <path d="M14,11.5A2.5,2.5 0 0,0 16.5,9A2.5,2.5 0 0,0 14,6.5A2.5,2.5 0 0,0 11.5,9A2.5,2.5 0 0,0 14,11.5M14,2C17.86,2 21,5.13 21,9C21,14.25 14,22 14,22C14,22 7,14.25 7,9A7,7 0 0,1 14,2M5,9C5,13.5 10.08,19.66 11,20.81L10,22C10,22 3,14.25 3,9C3,5.83 5.11,3.15 8,2.29C6.16,3.94 5,6.33 5,9Z" />
`;


const BioacousticPage: React.FC = () => {
  const { state, dispatch } = useAppState();

  // ── Project and Site state with mock hierarchy fallback ───────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [projectHierarchy] = useState(MOCK_PROJECT_HIERARCHY);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [activeSite, setActiveSite] = useState<BioSite>(MOCK_PROJECT_HIERARCHY.sites[0]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [activeDeployment, setActiveDeployment] = useState<BioDeployment>(
    MOCK_PROJECT_HIERARCHY.sites[0].deployments[0]
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'analysis' | 'catalog' | 'acoustic'>('analysis');

  const locState = location.state as any;
  const projectId = locState?.projectId || state.selectedProject?.projectDetails?.project_id || (state.projects && state.projects.length > 0 ? state.projects[0].project_id : null);

  const [projectDetails, setProjectDetails] = useState<ProjectWithSites | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  
  // Provide local state for selections, falling back to location state initially
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | undefined>(locState?.deploymentId);
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>(locState?.siteName);
  const [selectedDeploymentName, setSelectedDeploymentName] = useState<string | undefined>(locState?.deploymentName);
  const [deploymentsList, setDeploymentsList] = useState<any[]>([]);

  // Sync back if location changes
  useEffect(() => {
    if (locState?.deploymentId) setSelectedDeploymentId(locState.deploymentId);
    if (locState?.siteName) setSelectedSiteName(locState.siteName);
    if (locState?.deploymentName) setSelectedDeploymentName(locState.deploymentName);
  }, [locState]);

  useEffect(() => {
    if (locState?.tab) {
      if (locState.tab === 'acoustic') {
        setActiveTab('acoustic');
      }
    }
  }, [location]);

  // Species catalogue is Tata-only: hide its tab elsewhere and leave it if the project changes.
  const showSpeciesCatalog = isTataProject(
    projectDetails?.project_name,
    state.selectedProject?.projectDetails?.project_name,
    selectedSiteName,
    selectedDeploymentName,
  );

  useEffect(() => {
    if (activeTab === 'catalog' && !showSpeciesCatalog) setActiveTab('analysis');
  }, [activeTab, showSpeciesCatalog]);

  useEffect(() => {
    if (projectId) {
      const fetchProjectDetails = async () => {
        try {
          const res = await clientService.api.readProjectApiV1ProjectsProjectIdGet(projectId);
          if (res.data) {
            setProjectDetails(res.data as any);
            try {
              const sitesRes = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(projectId);
              setSites(sitesRes.data || (res.data as any).sites || []);
            } catch (err) {
              console.warn('Failed to fetch detailed sites for boundary', err);
              setSites((res.data as any).sites || []);
            }
          }
        } catch (e) {
          console.error('Failed to fetch project details', e);
        }
      };
      fetchProjectDetails();
    }
  }, [projectId]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // Staging's file list grows as soon as a batch lands, so a finished upload bumps the
  // key the acoustic table fetches on.
  const [audioRefreshKey, setAudioRefreshKey] = useState(0);

  // ── Audio Analyzer Popup State ───────────────────────────────────────────
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [analyzerFile, setAnalyzerFile] = useState<any>(null);

  // ── Species Avg Activity Trend Modal State ────────────────────────────────
  const [speciesActivityModalOpen, setSpeciesActivityModalOpen] = useState(false);
  const [selectedSpeciesForActivity, setSelectedSpeciesForActivity] = useState<any>(null);

  // ── Active Section In-View State for Hierarchy Tracking ───────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [currentSection, setCurrentSection] = useState<'project' | 'site' | 'deployment' | 'comparison'>('project');

  // ── Map refs ─────────────────────────────────────────────────────────────
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Renders SVG string onto canvas
  const createMarkerImageData = (svgString: string, size = 48): Promise<ImageData> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(url);
        resolve(ctx.getImageData(0, 0, size, size));
      };
      img.src = url;
    });
  };

  // ── Initialize map and deployment markers exactly like Project Overview ───
  useEffect(() => {
    if (!mapContainerRef.current || !projectDetails) return;
    
    mapboxgl.accessToken = mapBoxConfig.accessToken;
    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      center: [78.9629, 20.5937],
      zoom: 3.5,
      attributionControl: false,
    });
    mapRef.current = m;
    m.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const fetchAndSetMarkers = async () => {
      try {
        const bboxParam = '-180,-90,180,90';
        
        const response = await clientService.request<any[], any>({
          path: `/api/v1/projects/${projectDetails.project_id}/map/markers`,
          method: 'GET',
          query: { bbox: bboxParam, _t: Date.now() },
          format: 'json',
          secure: true,
        });
        
        const rawMarkers = response.data || [];
        const markersData = rawMarkers.filter((m: any) => {
          const type = (m.device_type || '').toLowerCase();
          return type === 'audio' || type === 'bioacoustic' || type === 'audiomoth' || (!type.includes('camera') && !type.includes('soil') && !type.includes('hydro'));
        });
        setDeploymentsList(markersData);
        setSelectedDeploymentId((prev) => {
          if (!prev && markersData.length > 0) {
            setSelectedDeploymentName(markersData[0].deployment_name);
            setSelectedSiteName(markersData[0].site_name);
            return markersData[0].deployment_id;
          }
          return prev;
        });
        const groupedMarkers = new Map<string, any[]>();
        markersData.forEach((m: any) => {
          const key = `${m.longitude},${m.latitude}`;
          if (!groupedMarkers.has(key)) {
             groupedMarkers.set(key, []);
          }
          groupedMarkers.get(key)!.push(m);
        });

        const features = Array.from(groupedMarkers.values()).map((group: any[]) => {
          const marker = group[0];
          return {
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: [marker.longitude, marker.latitude],
            },
            properties: {
              deployment_id: marker.deployment_id,
              deployment_name: marker.deployment_name,
              device_type: marker.device_type,
              site_name: marker.site_name,
              is_multiple: group.length > 1,
              deployments_json: group.length > 1 ? JSON.stringify(group) : undefined,
            },
          };
        });

        const source = m.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
        if (source) {
          source.setData({
            type: 'FeatureCollection',
            features: features,
          });
        }
      } catch (e) {
        console.error('Error fetching map markers:', e);
      }
    };

    m.on('load', async () => {
       const hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'site-hover-popup',
      });
      sites.forEach((site, index) => {
        // Draw Polygon boundary
        if (site.boundary) {
          try {
            addSiteBoundaryWithColor(m, site, getColorByIndex(index), {
              onMouseEnter: (e) => {
                m.getCanvas().style.cursor = 'pointer';
                if (e.features && e.features.length > 0) {
                  const props = e.features[0].properties;
                  hoverPopup
                    .setLngLat(e.lngLat)
                    .setHTML(
                      `
                      <div style="color:#fff;font-size:11px;padding:2px;">
                        <strong>${props?.Name || ''}</strong><br/>
                        Area: ${props?.Area || 0} ha
                      </div>
                    `,
                    )
                    .addTo(m);
                }
              },
              onMouseLeave: () => {
                m.getCanvas().style.cursor = '';
                hoverPopup.remove();
              },
            });
          } catch (err) {
            console.error('Error rendering site boundary:', err);
          }
        }
      });
      const markerDefs = [
        { id: 'marker-audio',    color: '#a855f7', icon: AUDIO_ICON_PATH },
        { id: 'marker-multiple', color: '#f59e0b', icon: MULTIPLE_MARKER_PATH },
      ];
      await Promise.all(
        markerDefs.map(async ({ id, color, icon }) => {
          if (!m.hasImage(id)) {
            const svg = buildMarkerSVG(color, icon);
            const imageData = await createMarkerImageData(svg, 52);
            m.addImage(id, imageData, { pixelRatio: 2 });
          }
        })
      );

      // 2. Add source and layers
      m.addSource('deployment-markers', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      m.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'deployment-markers',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': ['step', ['get', 'point_count'], '#51bbd6', 10, '#f1f075', 30, '#f28cb1'],
          'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40 ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        }
      });

      m.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'deployment-markers',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
        }
      });

      m.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'deployment-markers',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'is_multiple'], true], 'marker-multiple',
            'marker-audio'
          ],
          'icon-size': 0.85,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
        }
      });

      m.addLayer({
        id: 'selected-deployment-highlight-glow',
        type: 'circle',
        source: 'deployment-markers',
        filter: ['==', ['get', 'deployment_id'], selectedDeploymentId || ''],
        paint: {
          'circle-color': '#10B981',
          'circle-radius': 45,
          'circle-opacity': 0.3,
          'circle-blur': 0.8,
        }
      }, 'unclustered-point');

      m.addLayer({
        id: 'selected-deployment-highlight',
        type: 'circle',
        source: 'deployment-markers',
        filter: ['==', ['get', 'deployment_id'], selectedDeploymentId || ''],
        paint: {
          'circle-color': 'rgba(16, 185, 129, 0.1)',
          'circle-radius': 30,
          'circle-stroke-color': '#10B981',
          'circle-stroke-width': 3,
        }
      }, 'unclustered-point');

      // 3. Setup click and hover handlers
      m.on('click', 'clusters', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        if (!features.length) return;
        const clusterId = features[0].properties?.cluster_id;
        const source = m.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom === undefined || zoom === null) return;
          const coordinates = (features[0].geometry as any).coordinates;
          m.easeTo({ center: coordinates, zoom });
        });
      });

      m.on('click', 'unclustered-point', (e) => {
        const features = m.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] });
        if (!features.length) return;
        const props = features[0].properties;
        if (props) {
          let selectedProps = props;
          if (props.deployments_json) {
            try {
               const parsed = JSON.parse(props.deployments_json);
               if (parsed.length > 0) selectedProps = parsed[0];
            } catch (err) { /* ignore */ }
          }
          setSelectedDeploymentId(selectedProps.deployment_id);
          setSelectedSiteName(selectedProps.site_name);
          setSelectedDeploymentName(selectedProps.deployment_name);
          setActiveTab('acoustic');
        }
      });

      m.on('mouseenter', 'clusters', () => m.getCanvas().style.cursor = 'pointer');
      m.on('mouseleave', 'clusters', () => m.getCanvas().style.cursor = '');
      m.on('mouseenter', 'unclustered-point', () => m.getCanvas().style.cursor = 'pointer');
      m.on('mouseleave', 'unclustered-point', () => m.getCanvas().style.cursor = '');

      fetchAndSetMarkers();
      
      if (sites.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        let hasBounds = false;
        sites.forEach(site => {
          if (site.centroid_wkt) {
             const coords = site.centroid_wkt.replace('POINT (', '').replace(')', '').split(' ');
             bounds.extend([parseFloat(coords[0]), parseFloat(coords[1])]);
             hasBounds = true;
          }
        });
        if (hasBounds) m.fitBounds(bounds, { padding: 50, maxZoom: 12, duration: 800 });
      }
    });

    return () => {
      m.remove();
      mapRef.current = null;
    };
  }, [projectDetails, sites]);

  // Update highlight when selectedDeploymentId changes
  useEffect(() => {
    try {
      if (mapRef.current && mapRef.current.isStyleLoaded()) {
        if (mapRef.current.getLayer('selected-deployment-highlight-glow')) {
          mapRef.current.setFilter('selected-deployment-highlight-glow', ['==', ['get', 'deployment_id'], selectedDeploymentId || '']);
        }
        if (mapRef.current.getLayer('selected-deployment-highlight')) {
          mapRef.current.setFilter('selected-deployment-highlight', ['==', ['get', 'deployment_id'], selectedDeploymentId || '']);
        }
      }
    } catch (e) {
      console.warn("Could not update deployment highlight on map", e);
    }
  }, [selectedDeploymentId]);

  // ── Load projects list on mount if empty ──────────────────────────────────
  useEffect(() => {
    if (state.projects && state.projects.length > 0) return;
    const fetchProjects = async () => {
      try {
        const resp = await clientService.api.readProjectsApiV1ProjectsGet();
        const list: Project[] = resp.data || [];
        dispatch({ type: 'SET_PROJECT_LIST', payload: list });
      } catch (err) {
        console.error('BioacousticPage: failed to fetch projects', err);
      }
    };
    fetchProjects();
  }, [state.projects, dispatch]);

  // (Removed Mapbox API logic to use InteractiveMap component)

  // ── In-Page Section Scroll Jump inside scrollContainerRef ─────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const scrollToSection = (sectionId: string, sectionKey?: 'project' | 'site' | 'deployment' | 'comparison') => {
    if (sectionKey) setCurrentSection(sectionKey);
    const el = document.getElementById(sectionId);
    const container = scrollContainerRef.current;
    if (el && container) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offset = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({ top: Math.max(0, offset - 16), behavior: 'smooth' });
    }
  };

  // ── ScrollSpy to Track and Highlight Active Section in Hierarchy Bar ──────
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || activeTab !== 'analysis') return;

    const sectionIds: { id: string; key: 'project' | 'site' | 'deployment' | 'comparison' }[] = [
      { id: 'project-level-section', key: 'project' },
      { id: 'site-level-section', key: 'site' },
      { id: 'deployment-level-section', key: 'deployment' },
      { id: 'deployment-comparison-section', key: 'comparison' },
    ];

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;
      let active: 'project' | 'site' | 'deployment' | 'comparison' = 'project';
      for (const sec of sectionIds) {
        const el = document.getElementById(sec.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top - containerTop <= 160) {
            active = sec.key;
          }
        }
      }
      setCurrentSection(active);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab, projectHierarchy, activeSite, activeDeployment]);

  // ── Site & Deployment Selection Handlers ──────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleSelectSite = (site: BioSite) => {
    setActiveSite(site);
    if (site.deployments.length > 0) {
      setActiveDeployment(site.deployments[0]);
    }
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [site.longitude, site.latitude],
        zoom: 12.5,
        duration: 1000,
      });
    }
    if (activeTab === 'analysis') {
      scrollToSection('site-level-section');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleSelectDeployment = (deployment: BioDeployment) => {
    setActiveDeployment(deployment);
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [deployment.longitude, deployment.latitude],
        zoom: 13.5,
        duration: 1000,
      });
    }
    if (activeTab === 'analysis') {
      scrollToSection('deployment-level-section');
    }
  };

  // ── Open Species Avg Activity Trend Modal ─────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleOpenSpeciesActivityTrend = (species: any) => {
    setSelectedSpeciesForActivity(species);
    setSpeciesActivityModalOpen(true);
  };

  // ── Open Audio Analyzer Popup for Species Logs (Legacy) ───────────────────
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const handleAnalyzeSpeciesLogs = (species: any) => {
    const sample = species.audioSample || {
      fileName: `AM_20260105_${species.name.replace(/\s+/g, '_')}.wav`,
      duration: '15:00',
      timestamp: '2026-01-05 06:30:00 UTC',
      scientificName: species.name,
      detectedSpeciesList: [
        {
          name: species.commonName,
          scientificName: species.name,
          confidence: species.confidence,
          imageUrl: 'https://images.unsplash.com/photo-1552728089-57bdde30beb3?w=120&auto=format&fit=crop&q=80',
        },
      ],
    };

    setAnalyzerFile({
      fileName: sample.fileName,
      timestamp: sample.timestamp,
      duration: sample.duration,
      status: 'ANALYZED' as const,
      detections: sample.detectedSpeciesList,
    });
    setAnalyzerOpen(true);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#070707',
        overflow: 'hidden',
        fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
      }}
    >
      <style>{`
        .highcharts-contextmenu {
          background: #141414 !important;
          border: 1px solid #282828 !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 30px rgba(0,0,0,0.85) !important;
          padding: 6px !important;
          z-index: 9999 !important;
        }
        .highcharts-menu {
          background: #141414 !important;
          border: none !important;
          padding: 0 !important;
        }
        .highcharts-menu-item {
          color: #DDDDDD !important;
          font-size: 11px !important;
          font-family: 'Century Gothic', Poppins, Arial, sans-serif !important;
          padding: 7px 14px !important;
          border-radius: 5px !important;
          cursor: pointer !important;
          transition: background 0.15s, color 0.15s !important;
        }
        .highcharts-menu-item:hover {
          background: rgba(16, 185, 129, 0.15) !important;
          color: #10B981 !important;
        }
        .highcharts-contextbutton rect,
        .highcharts-button rect {
          fill: #141414 !important;
          stroke: #2A2A2A !important;
          rx: 6px !important;
          ry: 6px !important;
          transition: fill 0.2s, stroke 0.2s !important;
        }
        .highcharts-contextbutton:hover rect,
        .highcharts-button:hover rect {
          fill: #222222 !important;
          stroke: #10B981 !important;
        }
        .highcharts-contextbutton path,
        .highcharts-button path {
          stroke: #888888 !important;
        }
        .highcharts-contextbutton:hover path,
        .highcharts-button:hover path {
          stroke: #10B981 !important;
        }
      `}</style>
      <Header />

      {/* ── Main Body Split Layout ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 73px)',
          maxHeight: 'calc(100vh - 73px)',
          overflow: 'hidden',
          width: '100%',
          mt: '73px',
          flexDirection: { xs: 'column', md: 'row' }
        }}
      >
        {/* ── LEFT PANEL: Sticky Mapbox Satellite Map ────────────────────── */}
        <Box
          sx={{
            width: { xs: '100%', md: '440px', lg: '500px' },
            height: { xs: '35vh', md: '100%' },
            flexShrink: 0,
            position: 'relative',
            borderRight: { xs: 'none', md: '1px solid #1A1A1A' },
            borderBottom: { xs: '1px solid #1A1A1A', md: 'none' },
            background: '#040404',
          }}
        >
          {/* Map canvas */}
          <Box ref={mapContainerRef} sx={{ position: 'absolute', inset: 0 }} />

          {/* Map Bottom Metadata Overlay */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(0deg, rgba(4,4,4,0.98) 75%, transparent 100%)',
              p: 1.5,
              zIndex: 5,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography sx={{ fontSize: '9px', color: '#10B981', letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 800 }}>
                Active/Selected Deployment
              </Typography>
            </Box>

            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#fff', mb: 1 }}>
              {selectedDeploymentName || 'Select a deployment'}
            </Typography>

            <Box sx={{ pt: 1, borderTop: '1px solid #1A1A1A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '9px', color: '#10B981', letterSpacing: '0.6px', fontWeight: 800 }}>
                DARUKAA BIOACOUSTIC ENGINE
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── RIGHT PANEL: Tabbed Dashboard ─────────────────────────────── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Sub-Header Bar */}
          <Box sx={{ px: 3.5, pt: 2, pb: 1, borderBottom: '1px solid #1A1A1A', flexShrink: 0, bgcolor: '#090909' }}>
            {/* 3 Main View Tabs + Upload Data Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 3.5, alignItems: 'center' }}>
                <Button 
                  startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />} 
                  onClick={() => {
                    if (projectId) {
                      navigate(`/project/${projectId}/overview`);
                    } else {
                      navigate(-1);
                    }
                  }} 
                  sx={{ color: '#888', '&:hover': { color: '#10B981', bgcolor: 'transparent' }, textTransform: 'none', fontSize: '12px', minWidth: 'auto', p: 0, mr: 1 }}
                >
                  Back
                </Button>
                {[
                  { id: 'analysis', label: 'Analysis' },
                  ...(showSpeciesCatalog ? [{ id: 'catalog', label: 'Species Catalogue' }] : []),
                  { id: 'acoustic', label: 'Acoustic Data' },
                ].map((tab) => {
                  const isTabActive = activeTab === tab.id;
                  return (
                    <Box
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      sx={{
                        pb: 1,
                        cursor: 'pointer',
                        borderBottom: isTabActive ? '2px solid #10B981' : '2px solid transparent',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: isTabActive ? 800 : 500,
                          letterSpacing: '0.8px',
                          textTransform: 'uppercase',
                          color: isTabActive ? '#FFFFFF' : '#666666',
                          transition: 'color 0.2s',
                          '&:hover': { color: '#AAAAAA' },
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Upload Data Action */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setUploadModalOpen(true)}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 800,
                    background: '#10B981',
                    color: '#000',
                    px: 2,
                    height: '34px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderRadius: '4px',
                    boxShadow: 'none',
                    '&:hover': {
                      background: '#0D9268',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Upload Data
                </Button>
              </Box>
            </Box>

            {/* Hierarchy quick jump pills visible when on Analysis tab */}
            {/* {activeTab === 'analysis' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  pt: 1,
                  mt: 0.5,
                  borderTop: '1px solid #141414',
                  flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontSize: '10px', color: '#555', fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  Hierarchy View:
                </Typography>

                <Button
                  size="small"
                  startIcon={<ProjectIcon sx={{ fontSize: 13 }} />}
                  onClick={() => scrollToSection('project-level-section', 'project')}
                  sx={{
                    fontSize: '11px',
                    fontWeight: currentSection === 'project' ? 800 : 600,
                    color: currentSection === 'project' ? '#FFFFFF' : '#888888',
                    bgcolor: currentSection === 'project' ? 'rgba(16, 185, 129, 0.22)' : 'rgba(255, 255, 255, 0.02)',
                    border: currentSection === 'project' ? '1.5px solid #10B981' : '1px solid #222222',
                    boxShadow: currentSection === 'project' ? '0 0 12px rgba(16, 185, 129, 0.4)' : 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.35,
                    textTransform: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(16, 185, 129, 0.2)',
                      borderColor: '#10B981',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  1. Project Level
                </Button>

                <Button
                  size="small"
                  startIcon={<SiteIcon sx={{ fontSize: 13 }} />}
                  onClick={() => scrollToSection('site-level-section', 'site')}
                  sx={{
                    fontSize: '11px',
                    fontWeight: currentSection === 'site' ? 800 : 600,
                    color: currentSection === 'site' ? '#FFFFFF' : '#888888',
                    bgcolor: currentSection === 'site' ? 'rgba(0, 212, 255, 0.22)' : 'rgba(255, 255, 255, 0.02)',
                    border: currentSection === 'site' ? '1.5px solid #00D4FF' : '1px solid #222222',
                    boxShadow: currentSection === 'site' ? '0 0 12px rgba(0, 212, 255, 0.4)' : 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.35,
                    textTransform: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(0, 212, 255, 0.2)',
                      borderColor: '#00D4FF',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  2. Site Level ({activeSite.site_name.split(' - ')[1] || activeSite.site_name})
                </Button>

                <Button
                  size="small"
                  startIcon={<DeploymentIcon sx={{ fontSize: 13 }} />}
                  onClick={() => scrollToSection('deployment-level-section', 'deployment')}
                  sx={{
                    fontSize: '11px',
                    fontWeight: currentSection === 'deployment' ? 800 : 600,
                    color: currentSection === 'deployment' ? '#FFFFFF' : '#888888',
                    bgcolor: currentSection === 'deployment' ? 'rgba(139, 92, 246, 0.25)' : 'rgba(255, 255, 255, 0.02)',
                    border: currentSection === 'deployment' ? '1.5px solid #8B5CF6' : '1px solid #222222',
                    boxShadow: currentSection === 'deployment' ? '0 0 12px rgba(139, 92, 246, 0.45)' : 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.35,
                    textTransform: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(139, 92, 246, 0.2)',
                      borderColor: '#8B5CF6',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  3. Deployment Level ({activeDeployment.deployment_name.split(' (')[0]})
                </Button>

                <Button
                  size="small"
                  startIcon={<CompareIcon sx={{ fontSize: 13 }} />}
                  onClick={() => scrollToSection('deployment-comparison-section', 'comparison')}
                  sx={{
                    fontSize: '11px',
                    fontWeight: currentSection === 'comparison' ? 800 : 600,
                    color: currentSection === 'comparison' ? '#FFFFFF' : '#888888',
                    bgcolor: currentSection === 'comparison' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(255, 255, 255, 0.02)',
                    border: currentSection === 'comparison' ? '1.5px solid #F59E0B' : '1px solid #222222',
                    boxShadow: currentSection === 'comparison' ? '0 0 12px rgba(245, 158, 11, 0.45)' : 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.35,
                    textTransform: 'none',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(245, 158, 11, 0.2)',
                      borderColor: '#F59E0B',
                      color: '#FFFFFF',
                    },
                  }}
                >
                  4. Deployment Comparison
                </Button>
              </Box>
            )} */}
          </Box>

          {/* Scrollable Tab Content */}
          <Box
            ref={scrollContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3.5,
              py: 3,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#070707',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#222',
                borderRadius: '3px',
              },
            }}
          >
            {/* {activeTab === 'analysis' && (
              <>
                <ProjectLevelSection
                  project={projectHierarchy}
                  activeSiteId={activeSite.site_id}
                  onSelectSite={handleSelectSite}
                />

                <SiteLevelSection
                  sites={projectHierarchy.sites}
                  activeSite={activeSite}
                  onSelectSite={handleSelectSite}
                  activeDeploymentId={activeDeployment.deployment_id}
                  onSelectDeployment={handleSelectDeployment}
                />

                <DeploymentLevelSection
                  site={activeSite}
                  activeDeployment={activeDeployment}
                  onSelectDeployment={handleSelectDeployment}
                  onAvgActivityTrend={handleOpenSpeciesActivityTrend}
                />

                <ProjectDeploymentComparisonSection
                  project={projectHierarchy}
                />
              </>
            )} */}

            {activeTab === 'analysis' && (
              <BioacousticAnalysis
                deploymentId={selectedDeploymentId}
                siteName={selectedSiteName}
                deploymentName={selectedDeploymentName}
                deploymentsList={deploymentsList}
                onDeploymentSelect={(id, name, site) => {
                  setSelectedDeploymentId(id);
                  setSelectedDeploymentName(name);
                  setSelectedSiteName(site);
                  const dep = deploymentsList.find((d) => d.deployment_id === id);
                  if (dep && mapRef.current) {
                    mapRef.current.flyTo({ center: [dep.longitude, dep.latitude], zoom: 16, essential: true });
                  }
                }}
                onUploadClick={() => setUploadModalOpen(true)}
              />
            )}

            {activeTab === 'catalog' && showSpeciesCatalog && (
              <SpeciesCatalog
                projectName={projectDetails?.project_name || state.selectedProject?.projectDetails?.project_name}
                deploymentId={selectedDeploymentId}
                siteName={selectedSiteName}
                deploymentName={selectedDeploymentName}
                deploymentsList={deploymentsList}
                onDeploymentSelect={(id, name, site) => {
                  setSelectedDeploymentId(id);
                  setSelectedDeploymentName(name);
                  setSelectedSiteName(site);
                  const dep = deploymentsList.find((d) => d.deployment_id === id);
                  if (dep && mapRef.current) {
                    mapRef.current.flyTo({ center: [dep.longitude, dep.latitude], zoom: 16, essential: true });
                  }
                }}
                onUploadClick={() => setUploadModalOpen(true)}
              />
            )} 
            {activeTab === 'acoustic' && (
              <AcousticDataView
                onUploadClick={() => setUploadModalOpen(true)}
                deploymentId={selectedDeploymentId}
                siteName={selectedSiteName}
                deploymentName={selectedDeploymentName}
                deploymentsList={deploymentsList}
                onDeploymentSelect={(id, name, site) => {
                  setSelectedDeploymentId(id);
                  setSelectedDeploymentName(name);
                  setSelectedSiteName(site);
                  const dep = deploymentsList.find((d) => d.deployment_id === id);
                  if (dep && mapRef.current) {
                    mapRef.current.flyTo({ center: [dep.longitude, dep.latitude], zoom: 16, essential: true });
                  }
                }}
                refreshKey={audioRefreshKey}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Upload Audio Modal */}
      <UploadAudioModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        deploymentId={selectedDeploymentId}
        onUploaded={(results) => {
          if (results.some((r) => !r.error)) setAudioRefreshKey((k) => k + 1);
        }}
      />

      {/* Species Avg Activity Trend Modal */}
      <SpeciesAvgActivityModal
        open={speciesActivityModalOpen}
        onClose={() => setSpeciesActivityModalOpen(false)}
        species={selectedSpeciesForActivity}
      />

      {/* Audio Analyzer Modal (Preserved) */}
      <AudioAnalyzerPopup
        open={analyzerOpen}
        onClose={() => setAnalyzerOpen(false)}
        file={analyzerFile}
        deploymentId={selectedDeploymentId}
      />
    </Box>
  );
};

export default BioacousticPage;
